import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { selectFilters } from '../redux/filtersReducer';
import { selectLocation } from '../redux/locationReducer';
import { selectAppConfig } from '../redux/appConfigReducer';
import { FilterTypes, USED_COMBINED_FILTERS_LIST_TIER_3 } from '../constants/initialFilters';

const useFilterStringifier = (filters: FilterTypes) => {
  const { location } = useSelector(selectLocation);
  const { isTier3page, listOfDealers, marketType } = useSelector(selectAppConfig);
  // add array from partner dealers from temporaryFilters/confirmfilters
  const {
    availableInMonths,
    bodyType,
    drivetrain,
    exteriorColor,
    fuelType,
    gearBox,
    interiorColor,
    mileage,
    modelFamily,
    modelYear,
    partnerDealers,
    power,
    price,
    trimLine,
    usedInventoryType,
    vehicleType,
    wheelSize,
    nonAudi,
  } = filters;

  const locationString = useMemo(() => {
    if (isTier3page && partnerDealers?.length > 0) {
      return partnerDealers?.map((dealer) => `dealer.${dealer}`).join(',');
    }
    if (!location?.selectedUserLocation) return undefined;
    const { selectedUserLocation, dealershipList, mode } = location;
    const { latitude, longitude } = selectedUserLocation.userCoordinates;
    const radiusUnit = marketType === 'CAN' ? 'km' : 'miles';
    if (mode === 'BY_RADIUS') {
      const { radius } = location;
      return `geo:${latitude}_${longitude}_${radius}_${radiusUnit}_defaultcity`;
    }
    const geoRadiusFilter = `geo:${latitude}_${longitude}_10000_${radiusUnit}_defaultcity`;
    const selectedDealers = dealershipList
      .map(({ kvpsSyncId }: { kvpsSyncId: string }) => `dealer.${kvpsSyncId}`)
      .join(',');
    return `${selectedDealers},${geoRadiusFilter}`;
  }, [isTier3page, partnerDealers, location, marketType]);

  const vehicleTypeFiltes = useMemo(() => {
    const isNewVehicleSelected = vehicleType === 'new';
    if (isNewVehicleSelected && availableInMonths !== 3)
      return [`available-in-months_${availableInMonths}`];
    if (!isNewVehicleSelected && usedInventoryType === 'has-warranties.yes')
      return 'has-warranties.1';
    if (!isNewVehicleSelected && usedInventoryType === 'has-warranties.no')
      return 'has-warranties.0';
    return '';
  }, [availableInMonths, usedInventoryType, vehicleType]);

  const shouldFilterNonAudi = useMemo(() => {
    if (vehicleType === 'new') return false;
    if (nonAudi === 'foreign-brand.yes') return true;
    if (usedInventoryType === 'has-warranties.combined') return true;
    return false;
  }, [nonAudi, usedInventoryType, vehicleType]);

  const priceRange = price.length ? `prices.retail:${price.join('_')}` : '';
  const mileageRange = mileage.length ? `usedCarMileage:${mileage.join('_')}` : '';
  const modelYearRange = modelYear.length ? `salesModelYear:${modelYear.join('_')}` : '';
  const powerRange = power.length ? `powerHP:${power.join('_')}` : '';

  const bodyTypeString =
    bodyType.length && !shouldFilterNonAudi
      ? bodyType.map(
          (filter) => `body-type.${filter === 'allroad_quattro' ? 'allroad-quattro' : filter}`,
        )
      : '';
  const trimLineString =
    // No trimline filter for non audi vehicles or combined audi and Non Audi
    trimLine.length && !shouldFilterNonAudi ? trimLine.map((filter) => `trimline.${filter}`) : '';
  const gearBoxString =
    // No Gearbox filter for non audi vehicles or combined audi and Non Audi
    gearBox.length && !shouldFilterNonAudi ? gearBox.map((filter) => `gear-type.${filter}`) : '';
  const drivetrainString =
    // No DriveTrain filter for non audi vehicles or combined audi and Non Audi
    drivetrain.length && !shouldFilterNonAudi ? drivetrain.map((filter) => `drive.${filter}`) : '';
  const wheelSizeString =
    // No WheelSize filter for non audi vehicles or combined audi and Non Audi
    wheelSize.length && !shouldFilterNonAudi
      ? wheelSize.map((filter) => `wheel-size.${filter}`)
      : '';
  const fuelTypeString =
    // No Fuel Type filter for non audi vehicles or combined audi and Non Audi
    fuelType.length && !shouldFilterNonAudi
      ? fuelType.map((filter) => `vtp-drivetrain.${filter}`)
      : '';
  const exteriorColorString =
    // No Exterior Colour filter for non audi vehicles or combined audi and Non Audi
    exteriorColor.length && !shouldFilterNonAudi
      ? exteriorColor.map((filter) => `exterior-color.${filter}`)
      : '';
  const interiorColorString =
    // No Interior Color filter for non audi vehicles or combined audi and Non Audi
    interiorColor.length && !shouldFilterNonAudi
      ? interiorColor.map((filter) => `interior-color-seats.${filter}`)
      : '';

  const modelFamilyString =
    modelFamily.length && !shouldFilterNonAudi
      ? modelFamily.map((filter) => `model-range.${filter}`)
      : '';

  const otherMakesString =
    filters.otherMakes?.length && shouldFilterNonAudi && isTier3page
      ? filters.otherMakes?.map((filter) => `t_manuf.${filter}`)
      : '';

  const nonAudiModelsString =
    filters.nonAudiModels?.length &&
    filters.otherMakes?.length &&
    shouldFilterNonAudi &&
    isTier3page
      ? filters.nonAudiModels?.map((filter) => `t_model.${filter}`)
      : '';

  const combinedFilters = [
    vehicleTypeFiltes,
    locationString,
    modelFamilyString,
    bodyTypeString,
    trimLineString,
    gearBoxString,
    drivetrainString,
    wheelSizeString,
    fuelTypeString,
    exteriorColorString,
    interiorColorString,
    priceRange,
    mileageRange,
    modelYearRange,
    powerRange,
    otherMakesString,
    nonAudiModelsString,
    // t3DealersString,
  ]
    .filter(Boolean)
    .join(',');

  const shouldListVehicles = useMemo(() => {
    if (isTier3page && !!listOfDealers) return true;
    if (
      !!location?.isPartnerHeader ||
      !!location?.isPreSetPartner ||
      !!location?.selectedUserLocation
    )
      return true;
    return false;
  }, [
    isTier3page,
    listOfDealers,
    location?.isPartnerHeader,
    location?.isPreSetPartner,
    location?.selectedUserLocation,
  ]);

  return { shouldListVehicles, combinedFilters };
};

export default useFilterStringifier;
